import { AppShell, Burger, Group, Button, Text, Box, rem, Drawer, Stack, Transition } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconHome, IconInfoCircle, IconCalendarEvent, IconMedicalCross, IconMail } from '@tabler/icons-react';
import { useMantineTheme } from '@mantine/core';
import { Footer } from './Footer';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const location = useLocation();
  const [isPageLoading, setIsPageLoading] = useState(false);
  const theme = useMantineTheme();

  // Page loading effect on route changes
  useEffect(() => {
    setIsPageLoading(true);
    const timer = setTimeout(() => setIsPageLoading(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const commonButtonProps = (path: string) => ({
    component: Link as any,
    to: path,
    variant: isActive(path) ? 'filled' : 'subtle',
    color: isActive(path) ? theme.primaryColor : theme.colors.gray[3],
    radius: 'sm',
    fullWidth: true,
    onClick: closeDrawer
  });


  return (
    <AppShell
      header={{ height: { base: 60, sm: 60 } }}
      padding={{ base: 'sm', sm: 'md' }}
    >
      <AppShell.Header>
        {/* Mobile Header Layout */}
        <Box 
          h="100%" 
          px="md" 
          hiddenFrom="sm" 
          w="100%" 
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <Burger 
              opened={drawerOpened} 
              onClick={openDrawer} 
              size="sm" 
              color={theme.colors.gray[3]}
              aria-label="Open navigation menu"
            />
          </Box>
          
          <Box style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <Text 
              fw={700} 
              component={Link} 
              to="/"
              style={{ 
                textDecoration: 'none', 
                color: theme.colors.gold[5],
                fontSize: rem(18),
                whiteSpace: 'nowrap'
              }}
            >
              Lykke Terapien
            </Text>
          </Box>
          
          <Box style={{ flex: 1 }} />
        </Box>

        {/* Desktop Header Layout */}
        <Group h="100%" px={{ base: 'sm', sm: 'md' }} visibleFrom="sm" justify="space-between">
          <Group gap="xs">
            <Text 
              fw={700} 
              component={Link} 
              to="/"
              style={{ 
                textDecoration: 'none', 
                color: theme.colors.gold[5],
                fontSize: rem(18),
              }}
              className="app-logo"
            >
              Lykke Terapien
            </Text>
          </Group>
          <Group gap="xs">
            <Button {...commonButtonProps('/')} leftSection={<IconHome size={16}/>} fullWidth={false} size="sm">Forside</Button>
            <Button {...commonButtonProps('/om-mig')} leftSection={<IconInfoCircle size={16}/>} fullWidth={false} size="sm">Om mig</Button>
            <Button {...commonButtonProps('/behandlinger')} leftSection={<IconMedicalCross size={16}/>} fullWidth={false} size="sm">Behandlinger</Button>
            <Button {...commonButtonProps('/kontakt')} leftSection={<IconMail size={16}/>} fullWidth={false} size="sm">Kontakt</Button>
            <Button 
              component="a"
              href="https://lykke-terapien.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              variant="filled" 
              color="gold" 
              radius="sm" 
              fullWidth={false} 
              size="sm" 
              leftSection={<IconCalendarEvent size={16} />}
            >
              Book tid
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <Drawer 
        opened={drawerOpened} 
        onClose={closeDrawer} 
        title={
          <Box ta="center" w="100%">
            <Text fw={600} size="lg" c={theme.colors.gold[7]}>
              Menu
            </Text>
          </Box>
        } 
        padding="md" 
        size="xs" 
        hiddenFrom="sm"
        position="left"
        overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      >
        <Stack gap="sm">
          <Button {...commonButtonProps('/')} leftSection={<IconHome size={18}/>} size="md">Forside</Button>
          <Button {...commonButtonProps('/om-mig')} leftSection={<IconInfoCircle size={18}/>} size="md">Om mig</Button>
          <Button {...commonButtonProps('/behandlinger')} leftSection={<IconMedicalCross size={18}/>} size="md">Behandlinger</Button>
          <Button {...commonButtonProps('/kontakt')} leftSection={<IconMail size={18}/>} size="md">Kontakt</Button>
          <Button 
            component="a"
            href="https://lykke-terapien.planway.com/"
            target="_blank"
            rel="noopener noreferrer"
            variant="filled" 
            color="gold" 
            radius="sm" 
            fullWidth
            size="md"
            leftSection={<IconCalendarEvent size={18}/>}
            onClick={closeDrawer}
          >
            Book tid
          </Button>
        </Stack>
      </Drawer>

      <AppShell.Main>
        <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Transition mounted={!isPageLoading} transition="fade" duration={300} timingFunction="ease">
            {(styles) => (
              <Box style={styles} pt={{ base: 'sm', sm: 'md' }} pb={{ base: 'lg', sm: 'xl' }} flex="1">
                {children}
              </Box>
            )}
          </Transition>
          
          <Footer />
        </Box>

        {isPageLoading && (
          <Box 
            style={{
              position: 'fixed',
              top: 60,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <Box
              style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${theme.colors.gold[2]}`,
                borderTop: `3px solid ${theme.colors.gold[6]}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          </Box>
        )}
      </AppShell.Main>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Mobile-specific improvements */
        @media (max-width: 768px) {
          .app-logo {
            font-size: ${rem(16)} !important;
          }
          
          /* Fix header alignment */
          .mantine-AppShell-header {
            display: flex !important;
            align-items: center !important;
          }
          
          .mantine-AppShell-header .mantine-Group-root {
            height: 100% !important;
            align-items: center !important;
          }
          
          /* Center the drawer title */
          .mantine-Drawer-title {
            width: 100% !important;
            text-align: center !important;
          }
          
          /* Ensure drawer buttons are touch-friendly */
          .mantine-Drawer-body .mantine-Button-root {
            min-height: 48px !important;
            font-size: ${rem(14)} !important;
          }
          
          /* Better spacing for mobile */
          .mantine-AppShell-main {
            padding-left: ${theme.spacing.xs} !important;
            padding-right: ${theme.spacing.xs} !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .mantine-Group-root .mantine-Button-root {
            padding-left: ${theme.spacing.xs} !important;
            padding-right: ${theme.spacing.xs} !important;
            font-size: ${rem(13)} !important;
          }
        }
      `}</style>
    </AppShell>
  );
} 